import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Loader2, Eye, Plus, CheckCircle2, Scale, AlertTriangle, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROCEDURE_TYPE_LABELS = {
    OPPOSITION: 'Oposición',
    NULLITY: 'Nulidad',
    CANCELLATION: 'Cancelación',
    INFRINGEMENT: 'Infracción',
    TFJA: 'TFJA',
    AMPARO: 'Amparo',
    ADMINISTRATIVE: 'Administrativo',
    OTHER: 'Otro'
};

const PROCEDURE_STATUS_COLORS = {
    DRAFT: 'bg-gray-100 text-gray-700',
    ACTIVE: 'bg-blue-100 text-blue-700',
    SUSPENDED: 'bg-yellow-100 text-yellow-700',
    CLOSED: 'bg-green-100 text-green-700',
    ARCHIVED: 'bg-gray-100 text-gray-500'
};

const PROCEDURE_STATUS_LABELS = {
    DRAFT: 'Borrador',
    ACTIVE: 'Activo',
    SUSPENDED: 'Suspendido',
    CLOSED: 'Cerrado',
    ARCHIVED: 'Archivado'
};

const PROCEDURE_TYPE_COLORS = {
    OPPOSITION: 'bg-purple-100 text-purple-700',
    NULLITY: 'bg-red-100 text-red-700',
    CANCELLATION: 'bg-orange-100 text-orange-700',
    INFRINGEMENT: 'bg-pink-100 text-pink-700',
    TFJA: 'bg-indigo-100 text-indigo-700',
    AMPARO: 'bg-blue-100 text-blue-700',
    ADMINISTRATIVE: 'bg-teal-100 text-teal-700',
    OTHER: 'bg-gray-100 text-gray-700'
};

export const ClientProcedures = ({ clientId, token, folderId, onNewProcedure }) => {
    const navigate = useNavigate();
    const [procedures, setProcedures] = useState([]);
    const [trademarks, setTrademarks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (clientId) {
            fetchProcedures();
        }
    }, [clientId, folderId]);

    async function fetchProcedures() {
        try {
            const [proceduresRes, trademarksRes] = await Promise.all([
                axios.get(`${API}/procedures?client_id=${clientId}${folderId ? `&folder_id=${folderId}` : ''}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API}/trademarks?client_id=${clientId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);

            setProcedures(proceduresRes.data);
            setTrademarks(trademarksRes.data);
        } catch (error) {
            console.error('Error fetching procedures:', error);
            toast.error('Error al cargar procedimientos');
        } finally {
            setLoading(false);
        }
    }

    const getTrademarkName = (trademarkId) => {
        const tm = trademarks.find(t => t.id === trademarkId);
        return tm ? (tm.denominacion || tm.expediente) : trademarkId;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleViewProcedure = (procedureId) => {
        navigate(`/procedures/${procedureId}`);
    };

    const handleAddAction = (procedureId) => {
        // TODO: Open add action modal when implemented
        toast.info('Agregar acción en desarrollo');
    };

    const handleCloseProcedure = async (procedureId) => {
        // TODO: Open close procedure modal when implemented
        toast.info('Cerrar procedimiento en desarrollo');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    if (procedures.length === 0) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <Scale className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Este cliente no tiene procedimientos registrados</p>
                {onNewProcedure && (
                    <Button
                        onClick={onNewProcedure}
                        className="bg-gray-900 hover:bg-gray-800"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Nuevo Procedimiento
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                        Procedimientos
                    </h3>
                    <span className="text-sm text-gray-500">
                        {procedures.length} procedimiento{procedures.length !== 1 ? 's' : ''}
                    </span>
                </div>
                {onNewProcedure && (
                    <Button
                        size="sm"
                        onClick={onNewProcedure}
                        className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                        <Plus className="h-3 w-3 mr-1" />
                        Nuevo
                    </Button>
                )}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Tipo
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Título
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Autoridad
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Marcas Vinculadas
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Próximo Plazo
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Estado
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Responsable
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Acciones
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        {procedures.map(procedure => (
                            <tr
                                key={procedure.id}
                                className="hover:bg-gray-50 cursor-pointer transition-colors"
                                onClick={() => handleViewProcedure(procedure.id)}
                            >
                                {/* Type Badge */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Badge className={`${PROCEDURE_TYPE_COLORS[procedure.procedure_type]} border-0`}>
                                        {PROCEDURE_TYPE_LABELS[procedure.procedure_type]}
                                    </Badge>
                                </td>

                                {/* Title */}
                                <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900">
                                        {procedure.title}
                                    </div>
                                    {procedure.procedure_number && (
                                        <div className="text-xs text-gray-500">
                                            {procedure.procedure_number}
                                        </div>
                                    )}
                                </td>

                                {/* Authority */}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {procedure.authority || '-'}
                                </td>

                                {/* Linked Trademarks */}
                                <td className="px-6 py-4">
                                    {procedure.trademark_ids && procedure.trademark_ids.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {procedure.trademark_ids.slice(0, 2).map(tmId => (
                                                <Badge
                                                    key={tmId}
                                                    variant="outline"
                                                    className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200"
                                                >
                                                    {getTrademarkName(tmId)}
                                                </Badge>
                                            ))}
                                            {procedure.trademark_ids.length > 2 && (
                                                <Badge variant="outline" className="text-xs">
                                                    +{procedure.trademark_ids.length - 2}
                                                </Badge>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-sm text-gray-400">-</span>
                                    )}
                                </td>

                                {/* Next Deadline */}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {formatDate(procedure.deadline_date)}
                                </td>

                                {/* Status Badge */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Badge className={`${PROCEDURE_STATUS_COLORS[procedure.status]} border-0`}>
                                        {PROCEDURE_STATUS_LABELS[procedure.status]}
                                    </Badge>
                                </td>

                                {/* Responsible User */}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {procedure.responsible_user_id ? (
                                        <span>Usuario {procedure.responsible_user_id.slice(0, 8)}...</span>
                                    ) : (
                                        <span className="text-gray-400">Sin asignar</span>
                                    )}
                                </td>

                                {/* Actions Menu */}
                                <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="sm">
                                                •••
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="bg-white border-gray-200">
                                            <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                onClick={() => handleViewProcedure(procedure.id)}
                                                className="cursor-pointer"
                                            >
                                                <Eye className="h-4 w-4 mr-2" />
                                                Ver Detalle
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                onClick={() => handleAddAction(procedure.id)}
                                                className="cursor-pointer"
                                            >
                                                <Plus className="h-4 w-4 mr-2" />
                                                Agregar Acción
                                            </DropdownMenuItem>
                                            {procedure.status === 'ACTIVE' && (
                                                <>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem
                                                        onClick={() => handleCloseProcedure(procedure.id)}
                                                        className="cursor-pointer"
                                                    >
                                                        <CheckCircle2 className="h-4 w-4 mr-2" />
                                                        Cerrar Procedimiento
                                                    </DropdownMenuItem>
                                                </>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
